import React, { Dispatch, SetStateAction, useState } from 'react';
import { isEqual } from 'lodash';

import ValidationAlert from '../../../app/components/ValidationAlert';
import {
  CreateUpdateContractDto,
  SignaturePositionSchemaDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { ContractStatusEnum } from '../../../common/pokCore/validation/enums';
import { useAuth } from '../../../common/hooks/useAuth';

import { ContractGeneralForm } from './ContractGeneralForm';
import { ContractFormGeneralControls } from './ContractFormGeneralControls';
import { ContractTabsEnum } from './ContractTabsEnum';
import { AttachmentViewer } from './AttachmentViewer';

interface ContractGeneralTabProps {
  contract: CreateUpdateContractDto;
  originalContract?: CreateUpdateContractDto;
  contractId?: string;
  attachmentId?: string;
  setContract?: Dispatch<SetStateAction<CreateUpdateContractDto | undefined>>;
  readonly?: boolean;
  setNotSavedTab: Dispatch<SetStateAction<ContractTabsEnum | undefined>>;
  hasEstimateItemsWithAmounts: boolean;
  handleRefresh: (id?: string) => void;
  saveSignaturePosition: (
    positions: SignaturePositionSchemaDto[],
  ) => Promise<void>;
  clientLanguage?: string;
  saveErrors: string[];
  setSaveErrors: Dispatch<SetStateAction<string[]>>;
}

export const ContractGeneralTab: React.FC<ContractGeneralTabProps> = props => {
  const [isLoading, setIsLoading] = useState(false);
  const [showAttachment, setShowAttachment] = useState(true);

  const auth = useAuth();

  const propertyChange = (obj: Partial<CreateUpdateContractDto>) => {
    const updatedContract = { ...props.contract, ...obj };

    const dataChanged = !isEqual(
      {
        ...updatedContract,
      },
      { ...props.originalContract },
    );
    props.setNotSavedTab(dataChanged ? ContractTabsEnum.GENERAL : undefined);

    if (props.setContract) {
      props.setContract(updatedContract);
    }
  };

  const handleRefresh = (id?: string) => {
    props.handleRefresh(id);
    setShowAttachment(true);
  };

  const saveSignaturePositions = async (
    positions: SignaturePositionSchemaDto[],
  ) => {
    if (props.contractId) {
      await props.saveSignaturePosition(positions);
    } else {
      propertyChange({ signaturePositions: positions });
    }
  };

  return (
    <div className="d-flex">
      <div style={{ flex: 1, height: '100%' }} className="d-grid gap-4 mb-3">
        <ContractGeneralForm
          contract={props.contract}
          attachmentId={props.attachmentId}
          propertyChange={propertyChange}
          readOnly={
            props.readonly ||
            props.contract?.active === false ||
            (props.contract?.status !== ContractStatusEnum.Entered &&
              props.contract?.status !== ContractStatusEnum.Unsigned)
          }
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          hasEstimateItemsWithAmounts={props.hasEstimateItemsWithAmounts}
          showAttachment={showAttachment}
          setShowAttachment={setShowAttachment}
          clientLanguage={props.clientLanguage}
        />
        <ValidationAlert
          show={!!props.saveErrors.length}
          errors={props.saveErrors}
          className="mt-4"
        />

        <ContractFormGeneralControls
          id={props.contractId}
          contract={props.contract}
          readOnly={
            props.readonly ||
            props.contract?.active === false ||
            (props.contract?.status !== ContractStatusEnum.Entered &&
              props.contract?.status !== ContractStatusEnum.Unsigned)
          }
          handleRefresh={handleRefresh}
          setSaveErrors={props.setSaveErrors}
        />
      </div>
      <AttachmentViewer
        attachment={props.contract?.attachment}
        attachmentId={props.attachmentId}
        showAttachmentViewer={showAttachment}
        signaturePositions={props.contract.signaturePositions || []}
        saveSignaturePositions={saveSignaturePositions}
        status={props.contract?.status}
        authorizedOwner={
          props.contract?.authorizedOwnerId === auth.currentUser?.sub
        }
        withSignaturePosition={!props.contract?.signedOutsidePok}
        activeContract={props.contract?.active}
      />
    </div>
  );
};
